import { Bitski, Mainnet } from 'bitski';
import type { BitskiProviderShim } from 'bitski/lib/provider-shim';
import { Connector } from '@wagmi/core';
import { createWalletClient, custom, getAddress } from 'viem';
import type { Chain } from 'viem/chains';
import { Wallet } from '@rainbow-me/rainbowkit';

export interface BitskiWalletOptions {
  bitski: Bitski;
  chains: Chain[];
}

export interface BitskiWagmiConnectorOptions {
  id?: string;
  name?: string;
  bitski: Bitski;
  loginHint?: string;
  waas?: {
    enabled: boolean;
    userId?: string;
  };
}

export class BitskiWagmiConnector extends Connector<
  BitskiProviderShim,
  BitskiWagmiConnectorOptions
> {
  async getChainId(): Promise<number> {
    const provider = await this.getProvider();
    const result: any = await provider.request({ method: 'net_version' });
    console.log('net_version', result);
    return result as number;
  }
  async isAuthorized(): Promise<boolean> {
    return true;
  }

  // These never happen

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onAccountsChanged(): void {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onChainChanged(): void {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onDisconnect(): void {}

  readonly id;
  readonly name;
  readonly ready = true;

  bitski: Bitski;
  bitskiProvider?: BitskiProviderShim;
  loginHint?: string;
  waasEnabled: boolean;
  userId?: string;

  constructor({ chains, options }: { chains?: Chain[]; options: BitskiWagmiConnectorOptions }) {
    super({
      chains,
      options,
    });
    this.id = options.id ?? 'bitski';
    this.name = options.name ?? 'Bitski';

    this.bitski = options.bitski;
    this.loginHint = options.loginHint;
    this.waasEnabled = options.waas?.enabled ?? false;
    this.userId = options.waas?.userId;
  }

  async getProvider(): Promise<BitskiProviderShim> {
    return await this.getProviderInternal();
  }

  async getProviderInternal(config?: { chainId?: number }): Promise<BitskiProviderShim> {
    console.log('getProviderInternal', this.chains);
    const chainId = config?.chainId ?? this.chains[0].id ?? 1;

    const chain = this.chains.find((chain) => chain.id === chainId);

    const network = (chain && getNetwork(chain)) ?? {
      chainId: chainId,
      rpcUrl: `https://api.bitski.com/v1/web3/${chainId}`,
    };
    
    const provider = await this.bitski.getProvider({
      network,
      waas: {
        enabled: this.waasEnabled,
        userId: this.userId,
      },
    });

    this.bitskiProvider = provider;

    return provider;
  }

  async connect(config?: { chainId?: number }): Promise<any> {
    await this.bitski.start({ login_hint: this.loginHint, prompt: 'login' });

    // HACK! Fixed in next SDK release.
    // @ts-ignore
    console.warn('provider hack', this.bitski.provider);
    // @ts-ignore
    this.bitski.provider = null;

    const user = await this.bitski.getUser();
    let account = user.accounts && user.accounts[0];

    if (!account) {
      const provider = await this.getProviderInternal(config);
      const result: any = await provider.request({ method: 'eth_accounts', params: [] });
      console.log('eth_accounts', result)
      account = result[0];
    }
    const chain = this.chains.find((x) => x.id === config?.chainId);

    return {
      account: account as any,
      chain: {
        id: chain?.id ?? 1,
        unsupported: chain ? true : false,
      },
    };
  }

  async disconnect(): Promise<void> {
    await this.bitski.signOut();
  }

  async getAccount(): Promise<`0x${string}`> {
    const provider = await this.getProvider();
    const result: any = await provider.request({ method: 'eth_accounts' });
    console.log('eth_accounts', result);

    // return checksum address
    return getAddress(result[0] as string);
  }


  async getWalletClient(config?: { chainId?: number }) {
    const chainId = config?.chainId ?? parseInt(process.env.REACT_APP_MODULAR_GAME_CHAIN_ID!);
    const chain = this.chains.find((x) => x.id === chainId)!;
    const network = chain ? getNetwork(chain) : undefined;
    const provider = this.bitski.getProvider({
      network,
      waas: {
        enabled: this.waasEnabled,
        userId: this.userId,
      },
    });
    if (!provider) throw new Error('provider is required.');
    const account = await this.getAccount();
    const walletClient = createWalletClient({
      account,
      chain,
      transport: custom(provider),
    });

    return walletClient;
  }
}

function getNetwork(chain: Chain): { chainId: number; rpcUrl: string } {
  const chainId = chain.id;
  const rpcUrl = getBitskiRpcUrl(chain);
  return {
    chainId,
    rpcUrl,
  };
}

function getBitskiRpcUrl(chain: Chain): string {
  switch (chain.id) {
    case 1:
      return Mainnet.rpcUrl;
    default:
      return chain.rpcUrls.public.http[0];
  }
}

export const bitskiWallet = ({
  bitski,
  chains,
  ...options
}: BitskiWalletOptions): Wallet => ({
  id: "bitski",
  name: "Bitski",
  installed:
    !!bitski ||
    (typeof window !== "undefined" &&
      typeof window.ethereum !== "undefined" &&
      ((window.ethereum as any).isBitski === true ||
        !!window.ethereum.providers?.find((p: any) => p.isBitski === true))),
  iconUrl: "https://cdn.bitskistatic.com/docs-web/bitskiWallet.svg",
  iconBackground: "#fff",
  downloadUrls: {
    browserExtension:
      "https://chrome.google.com/webstore/detail/bitski/feejiigddaafeojfddjjlmfkabimkell",
    ios: "https://apps.apple.com/us/app/bitski-wallet/id1587199538",
  },
  createConnector: () => ({
    connector: new BitskiWagmiConnector({
      chains,
      options: {
        bitski,
        loginHint: "fa_" + btoa(process.env.REACT_APP_BITSKI_CLIENT_ID!),
        waas: {
          enabled: true,
        }
      }
    }),
  }),
});
